import { getUserData } from "@wix/ambassador-identity-users-v1-user/rpc";
import type { Designer } from "@wix/ambassador-picasso-editor-v1-designer/types";
import { listDesigners } from "@wix/ambassador-picasso-editor-v1-designer/rpc";
import { PanoramaProvider } from "@wix/fe-essentials-standalone";
import { Logger } from "@wix/wix-log";
import React, { useEffect } from "react";
import { useLoaderData, useRouteLoaderData } from "react-router";
import LandingPage from "../landingPage/components/LandingPage/LandingPage";
import { isLoggedIn } from "../landingPage/serverApis/identity";
import { getUserFullName } from "../utils/utils";
import { createPanoramaClient } from "../utils/panorama";
import { NEW_DESIGNER_MODAL_EXPERIMENT, OLD_DESIGNERS_IDS } from "../consts";

export interface AppContext {
  isLoggedIn: boolean;
  userDetails: UserDetails | undefined;
}

export const AppContextProvider = React.createContext<AppContext>({
  isLoggedIn: false,
  userDetails: undefined,
});

export const loader = async ({ context }: { context: any }) => {
  const logger = new Logger("picasso-landing-page");
  let designers: Designer[] = [];
  try {
    const designersResponse = await context.bootstrapContext.ambassador.request(
      listDesigners({}),
    );
    designers = designersResponse.designers;
  } catch (err) {
    logger.error(err);
  }

  const loggedIn = await isLoggedIn({ context, logger });
  if (!loggedIn) {
    return { isLoggedIn: loggedIn, designers, userDetails: undefined };
  }
  try {
    const userDetails = await context.bootstrapContext.ambassador.request(
      getUserData({ includeProfile: true }),
      context.request.aspects,
    );
    if (!userDetails) {
      return { isLoggedIn: loggedIn, designers, userDetails: undefined };
    }
    return {
      isLoggedIn: loggedIn,
      designers,
      userDetails: {
        id: userDetails.id,
        email: userDetails.email,
        profileImage: userDetails.profile?.profileImage ?? "",
        fullName: getUserFullName({
          firstName: userDetails.profile?.firstName ?? "",
          lastName: userDetails.profile?.lastName ?? "",
        }),
      },
    };
  } catch (err) {
    logger.error(err);
  }
  return { isLoggedIn: loggedIn, designers, userDetails: undefined };
};

const LandingPageRoute = () => {
  const { isLoggedIn, designers, userDetails } = useLoaderData<typeof loader>();
  const rootData = useRouteLoaderData("root");

  const isNewDesignerModalEnabled =
    rootData?.experimentBag?.[NEW_DESIGNER_MODAL_EXPERIMENT] === "true";

  const filteredDesigners = designers.filter((designer: Designer) =>
    isNewDesignerModalEnabled
      ? !OLD_DESIGNERS_IDS.includes(designer?.id ?? "")
      : OLD_DESIGNERS_IDS.includes(designer?.id ?? ""),
  );

  useEffect(() => {
    if (!document.querySelector('script[src*="wix-recorder"]')) {
      const wixRecorderElement = window.document.createElement("script");
      wixRecorderElement.src =
        "https://static.parastorage.com/unpkg-semver/wix-recorder@^1/app.bundle.min.js";
      wixRecorderElement.setAttribute("data-small-app", "true");
      window.document.body.appendChild(wixRecorderElement);
    }
  }, []);

  const panoramaClient = createPanoramaClient(userDetails);
  panoramaClient.reportLoadStart();

  return (
    <PanoramaProvider value={panoramaClient}>
      <AppContextProvider.Provider value={{ isLoggedIn, userDetails }}>
        <LandingPage designers={filteredDesigners} />
      </AppContextProvider.Provider>
    </PanoramaProvider>
  );
};

export default LandingPageRoute;
